/* eslint-disable react/no-unused-prop-types */
/* https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/no-unused-prop-types.md#false-positives-intermediate-variables */
import * as React from 'react';
import { connect } from 'react-redux';

import Page from 'components/page/Page';

import CampaignDashboardInfo from 'modules/campaigns/components/dashboard/CampaignDashboardInfo';
import CcmCampaignDetails from 'modules/campaigns/components/ccm/components/details/CcmCampaignDetails';

import { fetchCampaignBrief, resetCampaignBrief } from 'modules/campaigns/reducers/campaignBrief';
import { fetchCampaignOverview, resetCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import { updateV2CampaignStatus } from 'modules/campaigns/reducers/campaignDeliverables';
import { resetAssets } from 'modules/campaigns/reducers/campaignAssets';
import { searchOrganization, selectOrganizationById } from '../../organizations/reducers/organizations';
import { trackCampaignSwitch } from 'modules/tracking/utils/tracking';

import { getCampaignSubnavigation, getCcmCampaignSubnavigation } from 'utils/navigation';
import { isBrandsFocusedOrganization } from 'utils/organization';
import { isCcmModerator, isInfluencerRealm } from 'utils/account';

import type {
  Account,
  Campaign,
  CampaignOverview,
  CampaignPageType,
  Organization,
  Params,
  Role
} from 'types';

type OwnProps = {
  documentTitle: string,
  children: React.ReactNode | undefined | null,
  showSubnavigation?: boolean,
  pageType: CampaignPageType,
  params: Params
};

type DispatchProps = {
  dispatchFetchCampaignOverview: (a: number) => Promise<any>,
  dispatchFetchCampaignBrief: (a: number) => Promise<any>,
  dispatchResetCampaignOverview: () => Promise<any>,
  dispatchResetCampaignBrief: () => Promise<any>,
  dispatchResetAssets: () => Promise<any>,
  dispatchSearchOrganization: (a: number) => Promise<any>,
  dispatchSelectOrganizationById: (a: number) => Promise<any>,
  dispatchUpdateV2CampaignStatus: (campaignId: number, isSCP: boolean) => Promise<any>
};

type StateProps = {
  organization: Organization,
  currentAccount: Account,
  roles: Array<Role>,
  campaignId: number,
  campaign: Campaign,
  overview: CampaignOverview | undefined | null,
  campaignProductListAccessIds: string,
  isSCP: boolean
};

type Props = StateProps & OwnProps & DispatchProps;

class BaseCampaignPage extends React.PureComponent<Props> {
  static defaultProps = {
    showSubnavigation: true
  };

  componentDidMount() {
    const { currentAccount, organization, campaignId } = this.props;

    trackCampaignSwitch(currentAccount, organization, campaignId);
    this.fetchCampaign(this.props);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.campaignId !== this.props.campaignId) {
      this.fetchCampaign(this.props);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetCampaignOverview,
      dispatchResetCampaignBrief,
      dispatchResetAssets
    } = this.props;

    dispatchResetCampaignOverview();
    dispatchResetCampaignBrief();
    dispatchResetAssets();
  }

  fetchCampaign(nextProps: Props): void {
    const {
      campaignId,
      isSCP,
      dispatchFetchCampaignBrief,
      dispatchSearchOrganization,
      dispatchSelectOrganizationById,
      dispatchFetchCampaignOverview,
      dispatchUpdateV2CampaignStatus
    } = nextProps;

    // used only to hide Reporting V2 subnav link (COLLAB-11918)
    dispatchUpdateV2CampaignStatus(campaignId, isSCP);

    dispatchFetchCampaignBrief(campaignId)
      .then(() => dispatchFetchCampaignOverview(campaignId))
      .then(() => dispatchSearchOrganization(campaignId))
      .then(organization => organization && organization.id &&
        dispatchSelectOrganizationById(organization.id)
      );
  }

  renderTopBlock(): React.ReactNode {
    const { pageType, campaign } = this.props;
    const isCcm = campaign.campaignType === 'ccm';
    const hideSpecifics = pageType === 'payments'
      || pageType === 'report'
      || pageType === 'content'
      || pageType === 'messenger';

    if (isCcm) {
      return (
        <CcmCampaignDetails
          campaign={campaign}
          hideSpecifics={hideSpecifics}
        />
      );
    }

    return (
      <CampaignDashboardInfo
        pageType={pageType}
      />
    );
  }

  render(): React.ReactNode {
    const {
      pageType,
      campaign,
      documentTitle,
      children,
      roles,
      organization,
      overview,
      showSubnavigation,
      campaignProductListAccessIds
    } = this.props;

    const campaignId = campaign ? campaign.id : null;

    const hideTopBlock = pageType === 'participants' || pageType === 'report-metrics';
    let topBlock: React.ReactNode | null = this.renderTopBlock();

    if (hideTopBlock) {
      topBlock = null;
    }

    let subnavigation;
    const isModerator = isCcmModerator(roles);

    if (campaign.campaignType === 'ccm') {
      subnavigation = getCcmCampaignSubnavigation(
        campaignId,
        isModerator
      );
    } else {
      subnavigation = getCampaignSubnavigation(
        campaignId,
        overview,
        organization,
        campaignProductListAccessIds
      );
    }

    return (
      <Page
        documentTitle={documentTitle}
        topBlock={topBlock}
        subnavigation={showSubnavigation
          ? subnavigation
          : null}
      >
        {children}
      </Page>
    );
  }
}

const mapStateToProps = (
  { account, organizations, campaignBrief, campaignOverview, runtimeConfig },
  {
    params
  }: OwnProps
): StateProps => {
  const isForBrand = organizations && isBrandsFocusedOrganization(organizations.currentOrganization);

  return {
    currentAccount: account.currentAccount,
    roles: account.roles,
    organization: organizations.currentOrganization,
    campaign: campaignBrief.brief,
    campaignId: Number(params.id),
    overview: isForBrand
      ? campaignOverview.overview
      : null,
    campaignProductListAccessIds: runtimeConfig?.config?.campaignProductListAccessIds ?? '',
    isSCP: isInfluencerRealm(account.roles)
      && runtimeConfig.config.zalandoOutfitTaskTypeEnabled
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaignOverview: (campaignId: number) => dispatch(fetchCampaignOverview(campaignId)),
  dispatchFetchCampaignBrief: (campaignId: number) => dispatch(fetchCampaignBrief(campaignId)),
  dispatchResetCampaignOverview: () => dispatch(resetCampaignOverview()),
  dispatchResetCampaignBrief: () => dispatch(resetCampaignBrief()),
  dispatchResetAssets: () => dispatch(resetAssets()),
  dispatchSearchOrganization: (campaignId) => dispatch(searchOrganization('campaign', campaignId)),
  dispatchSelectOrganizationById: (organizationId: number) => dispatch(selectOrganizationById(organizationId)),
  dispatchUpdateV2CampaignStatus: (campaignId: number, isSCP: boolean) => (
    dispatch(updateV2CampaignStatus(campaignId, isSCP))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(BaseCampaignPage);
